import React, { useEffect, useRef, useCallback } from 'react';
import { gate } from '@/lib/tripwire';
import { overlayConfig, getConfigWithOverrides } from '../config/poseOverlay.config';
import { smoothLandmarks, smoothLandmarksAdvanced, resetSmoothingState } from '../lib/pose/stabilize';
import { StillnessDetector } from '../lib/pose/detectStillness';
import { PhaseController, calculateDynamicParams } from '../lib/pose/warmup';
import { resampleTelemetry, landmarkFrameToTimed, timedToLandmarkFrame, analyzeTiming } from '../lib/pose/resampleTelemetry';
import { getContentRect, contentRectToCanvas } from '../lib/video/getContentRect';
import { 
  normToPixels, 
  mediapiperToNormalized, 
  applyMirroring,
  type PixelLandmark 
} from '../lib/pose/mapToPixels';
import { bodyScalePx, calculateProportionalSizes } from '../lib/pose/scaleFromBody';

// MediaPipe connection maps for skeleton rendering
// Body part specific connections for color coding
export const HIP_CONNECTIONS: [number, number][] = [
  [11,12],[11,23],[12,24],[23,24]  // hip box
];

export const SHOULDER_TORSO_CONNECTIONS: [number, number][] = [
  [11,12]  // shoulder line (already in hips but we'll draw it as torso)
];

export const ARM_CONNECTIONS: [number, number][] = [
  [11,13],[13,15],[15,17],[15,19],[15,21],  // left arm
  [12,14],[14,16],[16,18],[16,20],[16,22]   // right arm
];

export const LEG_CONNECTIONS: [number, number][] = [
  [23,25],[25,27],[27,29],[27,31],[29,31],  // left leg
  [24,26],[26,28],[28,30],[28,32],[30,32]   // right leg
];

export const HAND_CONNECTIONS: [number, number][] = [
  [0,1],[1,2],[2,3],[3,4],
  [0,5],[5,6],[6,7],[7,8],
  [0,9],[9,10],[10,11],[11,12],
  [0,13],[13,14],[14,15],[15,16],
  [0,17],[17,18],[18,19],[19,20],
  [5,9],[9,13],[13,17]
];

type LandmarkFrame = {
  t: number;
  width: number;
  height: number;
  pose?: {
    landmarks?: Array<Array<[number, number, number, number?]>>;
  };
  hands?: {
    landmarks?: Array<Array<[number, number, number, number?]>>;
  };
};

type Props = {
  videoRef: React.RefObject<HTMLVideoElement>;
  getLatestFrame: () => LandmarkFrame | null;
  getFrameAtTime: (time: number) => LandmarkFrame | null;
  isLiveCapture: boolean;
  visible: boolean;
  opacity: number; // 0..100
  handsOnly?: boolean;
  showSkeleton?: boolean; // default true
  /**
   * When true, map stored landmarks directly to video content rect with no stillness/smoothing.
   * Use for replay from DB pose — live-capture smoothing drifts and misaligns on static frames.
   */
  replayDirectMap?: boolean;
};

function getDisplayMetrics(videoEl: HTMLVideoElement) {
  const rect = videoEl.getBoundingClientRect();
  const cssW = rect.width;
  const cssH = rect.height;
  const dpr = window.devicePixelRatio || 1;
  const canvasW = Math.round(cssW * dpr);
  const canvasH = Math.round(cssH * dpr);
  return { cssW, cssH, canvasW, canvasH, dpr };
}

function drawSkeleton(
  ctx: CanvasRenderingContext2D,
  landmarks: Array<[number, number, number, number?]>,
  connections: [number, number][],
  scaleX: number,
  scaleY: number,
  color: string = "rgba(0,255,0,0.6)"
) {
  if (!landmarks || landmarks.length === 0) return;
  
  ctx.beginPath();
  for (const [a, b] of connections) {
    const A = landmarks[a];
    const B = landmarks[b];
    if (A && B) {
      ctx.moveTo(A[0] * scaleX, A[1] * scaleY);
      ctx.lineTo(B[0] * scaleX, B[1] * scaleY);
    }
  }
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.lineCap = "round";
  ctx.stroke();
}


export default function OverlayCanvas({ videoRef, getLatestFrame, getFrameAtTime, isLiveCapture, visible, opacity, handsOnly = false, showSkeleton = true, replayDirectMap = false }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const lastFrameTimeRef = useRef<number>(0);
  const frameCountRef = useRef<number>(0);
  
  // Advanced jitter reduction instances
  const stillnessDetectorRef = useRef<StillnessDetector | null>(null);
  const phaseControllerRef = useRef<PhaseController | null>(null);
  const lastMediaTimeRef = useRef<number>(0);

  const draw = useCallback(() => {
    // FIX: Guard all ref access
    const video = videoRef?.current;
    const canvas = canvasRef?.current;
    
    // TRIPWIRE: Overlay visibility diagnostics
    const overlayCheck = gate('overlay_draw', [
      { ok: !!video, msg: 'video_ref_missing', meta: { hasVideoRef: !!videoRef?.current } },
      { ok: !!canvas, msg: 'canvas_ref_missing', meta: { hasCanvasRef: !!canvasRef?.current } },
      { ok: visible, msg: 'overlay_not_visible', meta: { visible } },
      { ok: !!getLatestFrame, msg: 'getLatestFrame_missing', meta: { hasGetLatestFrame: !!getLatestFrame } }
    ]);
    
    if (!video || !canvas || !visible) return;

    const now = performance.now();
    const frameTime = now - lastFrameTimeRef.current;
    lastFrameTimeRef.current = now;
    frameCountRef.current++;

    // Get enhanced configuration with URL overrides
    const config = getConfigWithOverrides(overlayConfig);
    
    // S-3050A: Apply moderate smoothing for replay mode (interpolation handles most jitter)
    if (!isLiveCapture) {
      config.oneEuro.minCutoff = 0.5;  // Moderate smoothing (interpolation does heavy lifting)
      config.oneEuro.beta = 0.005;     // Light velocity response
      config.jitter.stillness.threshold = 1.0; // Standard threshold
    }
    
    // FIX: Initialize jitter reduction instances if needed (safe)
    if (!stillnessDetectorRef.current) {
      stillnessDetectorRef.current = new StillnessDetector({
        ...config.jitter.stillness,
        anchors: config.jitter.stillness.anchors as ('leftShoulder' | 'rightShoulder' | 'leftHip' | 'rightHip')[]
      });
    }
    if (!phaseControllerRef.current) {
      phaseControllerRef.current = new PhaseController(config.jitter.warmup);
    }

    // CORRECT GEOMETRY: Calculate actual video content rectangle
    const contentRect = getContentRect(video);
    const canvasContentRect = contentRectToCanvas(contentRect);
    
    // Set up canvas with proper DPR handling
    const dpr = window.devicePixelRatio || 1;
    const canvasW = Math.round(video.clientWidth * dpr);
    const canvasH = Math.round(video.clientHeight * dpr);
    
    if (canvas.width !== canvasW || canvas.height !== canvasH) {
      canvas.width = canvasW;
      canvas.height = canvasH;
      canvas.style.width = `${video.clientWidth}px`;
      canvas.style.height = `${video.clientHeight}px`;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Set up high-DPI rendering with correct transform
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, video.clientWidth, video.clientHeight);

    // Get current frame with time-based selection
    const frame = isLiveCapture ? getLatestFrame() : getFrameAtTime(video.currentTime);
    if (!frame) return;

    // EMERGENCY FIX (S-3050A): Check for dimension correction metadata
    const dimensionCorrection = (frame as any)?._dimensionCorrection;
    
    // DEBUG: Log geometry and dimension correction every 120 frames
    if (frameCountRef.current % 120 === 0) {
      console.log('[OverlayCanvas] Geometry:', {
        videoNative: { w: video.videoWidth, h: video.videoHeight },
        videoClient: { w: video.clientWidth, h: video.clientHeight },
        contentRect,
        dimensionCorrection: dimensionCorrection || 'none',
        canvasSize: { w: canvas.width, h: canvas.height }
      });
      
      // Check for aspect ratio mismatch
      if (dimensionCorrection) {
        const poseAspect = dimensionCorrection.poseWidth / dimensionCorrection.poseHeight;
        const videoAspect = video.videoWidth / video.videoHeight;
        
        if (Math.abs(poseAspect - videoAspect) > 0.01) {
          console.warn('[OverlayCanvas] Aspect ratio mismatch!', {
            poseAspect,
            videoAspect,
            poseDims: { w: dimensionCorrection.poseWidth, h: dimensionCorrection.poseHeight },
            videoDims: { w: video.videoWidth, h: video.videoHeight }
          });
        }
      }
    }

    // CRITICAL DEBUG: Check frame structure AND sample landmark
    if (frameCountRef.current % 30 === 0) {
      const sampleLandmark = frame.pose?.landmarks?.[0]?.[0]; // First landmark (nose usually)
      console.log('[OverlayCanvas] Frame structure:', {
        hasFrame: !!frame,
        hasPose: !!frame.pose,
        hasLandmarks: !!frame.pose?.landmarks,
        landmarksLength: frame.pose?.landmarks?.length,
        landmarksFirstElement: frame.pose?.landmarks?.[0],
        firstLandmarkCount: frame.pose?.landmarks?.[0]?.length,
        sampleNormalizedLandmark: sampleLandmark,
        showSkeleton,
        handsOnly
      });
    }

    // ADVANCED JITTER REDUCTION: Process landmarks with dynamic smoothing
    let pixelLandmarks: PixelLandmark[] = [];
    if (frame.pose?.landmarks && frame.pose.landmarks.length > 0) {
      // Replay from DB: no smoothing — map 0–1 landmarks straight to content rect (matches body)
      if (replayDirectMap) {
        const normalizedLandmarks = mediapiperToNormalized(frame.pose.landmarks[0]);
        pixelLandmarks = normToPixels(normalizedLandmarks, contentRect);
        pixelLandmarks = applyMirroring(pixelLandmarks, contentRect, false);
      } else {
      // Convert MediaPipe format to normalized landmarks
      const normalizedLandmarks = mediapiperToNormalized(frame.pose.landmarks[0]);
      
      // Map to pixel space for stillness detection
      const tempPixelLandmarks = normToPixels(normalizedLandmarks, contentRect);
      
      // DEBUG: Log sample pixel coordinates every 120 frames
      if (frameCountRef.current % 120 === 0) {
        console.log('[OverlayCanvas] Sample pixel coords:', {
          normalized: normalizedLandmarks[0],
          pixel: tempPixelLandmarks[0],
          contentRect,
          videoDisplay: { w: video.clientWidth, h: video.clientHeight }
        });
      }
      
      // FIX: Detect stillness and phase with null guards
      const mediaTime = video.currentTime;
      const stillnessDetector = stillnessDetectorRef.current;
      const phaseController = phaseControllerRef.current;
      if (!stillnessDetector || !phaseController) return;
      
      const { isStill, medianVel, phase } = stillnessDetector.step(
        tempPixelLandmarks,
        mediaTime,
        now
      );
      
      const phaseInfo = phaseController.step(mediaTime, isStill);
      
      // Calculate dynamic smoothing parameters
      const dynamicParams = calculateDynamicParams(
        config.oneEuro,
        phaseInfo,
        isStill,
        config.jitter.stillness.deltas
      );
      
      // Clamp dt for stability
      const rawDt = mediaTime - lastMediaTimeRef.current;
      const clampedDt = Math.max(config.jitter.dtClamp.min, Math.min(config.jitter.dtClamp.max, rawDt));
      lastMediaTimeRef.current = mediaTime;
      
      // Apply advanced smoothing in normalized space
      const smoothedNormalized = smoothLandmarksAdvanced(
        normalizedLandmarks.map(lm => [lm.x, lm.y, lm.z, lm.visibility]),
        config,
        now,
        {
          minCutoff: dynamicParams.minCutoff,
          beta: dynamicParams.beta,
          dCutoff: dynamicParams.dCutoff,
          phase,
          perJointMultipliers: config.jitter.perJoint
        }
      );
      
      // Convert back to normalized format
      const smoothedNormalizedLandmarks = smoothedNormalized.map(([x, y, z, visibility]) => ({
        x, y, z: z || 0, visibility: visibility || 1
      }));
      
      // Map to actual video content pixels (excludes letterbox)
      pixelLandmarks = normToPixels(smoothedNormalizedLandmarks, contentRect);
      
      // Apply mirroring if needed (currently disabled - no front-facing camera)
      pixelLandmarks = applyMirroring(pixelLandmarks, contentRect, false);
      
      // Debug logging (reduced frequency)
      if (frameCountRef.current % 120 === 0) {
        console.log(`[Advanced Jitter] phase=${phase} still=${isStill} vel=${medianVel.toFixed(1)}px/s dt=${clampedDt.toFixed(3)}s params={minCutoff:${dynamicParams.minCutoff.toFixed(2)},beta:${dynamicParams.beta.toFixed(4)}}`);
      }
      }
    }

    // CORRECT PROPORTIONAL SIZING: Calculate body scale from pixel landmarks
    const bodyScale = bodyScalePx(pixelLandmarks);
    const { strokePx, jointPx } = calculateProportionalSizes(bodyScale, {
      baseStrokePx: config.style.baseStrokePx,
      baseJointPx: config.style.baseJointPx,
      minStrokePx: config.normalization.minStrokePx,
      maxStrokePx: config.normalization.maxStrokePx,
      minJointPx: config.normalization.minJointPx,
      maxJointPx: config.normalization.maxJointPx
    });

    // Apply global opacity
    const normalizedOpacity = Math.max(0, Math.min(1, opacity / 100));

    // IMPROVED DRAWING: High-contrast, proportional rendering
    if (showSkeleton && !handsOnly && pixelLandmarks.length > 0) {
      // Setup for clean drawing
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      // Draw torso first (base layer)
      ctx.save();
      ctx.strokeStyle = config.colors.torso;
      ctx.lineWidth = strokePx;
      ctx.globalAlpha = normalizedOpacity * config.style.strokeAlpha;
      ctx.beginPath();
      
      for (const [a, b] of config.connections.torso) {
        const lmA = pixelLandmarks[a];
        const lmB = pixelLandmarks[b];
        if (lmA && lmB && lmA.visibility > 0.3 && lmB.visibility > 0.3) {
          ctx.moveTo(lmA.x, lmA.y);
          ctx.lineTo(lmB.x, lmB.y);
        }
      }
      ctx.stroke();
      ctx.restore();
      
      // Draw limbs with depth ordering (far to near)
      const limbConfigs = [
        { connections: config.connections.leftArm, color: config.colors.leftArm, width: strokePx * 0.9 },
        { connections: config.connections.leftLeg, color: config.colors.leftLeg, width: strokePx * 0.9 },
        { connections: config.connections.rightArm, color: config.colors.rightArm, width: strokePx },
        { connections: config.connections.rightLeg, color: config.colors.rightLeg, width: strokePx }
      ];
      
      for (const { connections, color, width } of limbConfigs) {
        ctx.save();
        ctx.strokeStyle = color;
        ctx.lineWidth = width;
        ctx.globalAlpha = normalizedOpacity * config.style.strokeAlpha;
        ctx.beginPath();
        
        for (const [a, b] of connections) {
          const lmA = pixelLandmarks[a];
          const lmB = pixelLandmarks[b];
          if (lmA && lmB && lmA.visibility > 0.3 && lmB.visibility > 0.3) {
            ctx.moveTo(lmA.x, lmA.y);
            ctx.lineTo(lmB.x, lmB.y);
          }
        }
        ctx.stroke();
        ctx.restore();
      }
      
      // Draw joints on top with proportional sizing
      ctx.save();
      ctx.fillStyle = config.colors.joints;
      ctx.globalAlpha = normalizedOpacity * config.style.jointAlpha;
      
      for (const landmark of pixelLandmarks) {
        if (landmark && landmark.visibility > 0.3) {
          ctx.beginPath();
          ctx.arc(landmark.x, landmark.y, jointPx, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.restore();
    }

    // Draw hands with correct coordinate mapping
    if (frame.hands?.landmarks) {
      for (let i = 0; i < frame.hands.landmarks.length; i++) {
        const handLandmarks = frame.hands.landmarks[i];
        const color = config.colors.hands[i === 0 ? 'left' : 'right'];
        
        // Map hand landmarks to pixels
        const handNormalized = mediapiperToNormalized(handLandmarks);
        const handPixels = normToPixels(handNormalized, contentRect);
        
        // Draw hand skeleton connections
        if (showSkeleton) {
          ctx.save();
          ctx.strokeStyle = color;
          ctx.lineWidth = strokePx * 0.7;
          ctx.lineCap = 'round';
          ctx.globalAlpha = normalizedOpacity * config.style.strokeAlpha;
          
          ctx.beginPath();
          for (const [a, b] of HAND_CONNECTIONS) {
            const lmA = handPixels[a];
            const lmB = handPixels[b];
            
            if (lmA && lmB) {
              ctx.moveTo(lmA.x, lmA.y);
              ctx.lineTo(lmB.x, lmB.y);
            }
          }
          ctx.stroke();
          ctx.restore();
        }
        
        // Draw hand joints
        ctx.save();
        ctx.fillStyle = color;
        ctx.globalAlpha = normalizedOpacity * config.style.jointAlpha;
        
        for (const handLandmark of handPixels) {
          ctx.beginPath();
          ctx.arc(handLandmark.x, handLandmark.y, jointPx * 0.5, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }
    }

    // DEBUG OVERLAY (development only)
    if (import.meta.env.DEV) {
      ctx.save();
      ctx.globalAlpha = 1.0;
      
      // Show content rectangle bounds
      if (config.debug.showBounds || config.debug.showScale) {
        ctx.strokeStyle = 'red';
        ctx.lineWidth = 2;
        ctx.strokeRect(contentRect.x, contentRect.y, contentRect.w, contentRect.h);
        
        // Debug info text
        ctx.fillStyle = 'white';
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 3;
        ctx.font = '14px monospace';
        
        // FIX: Enhanced debug information with safe access
        const phaseStats = phaseControllerRef.current?.getStats();
        const stillnessStats = stillnessDetectorRef.current?.getStats();
        
        const debugInfo = [
          `Body: ${bodyScale.toFixed(1)}px`,
          `Stroke: ${strokePx.toFixed(1)}px`,
          `Joint: ${jointPx.toFixed(1)}px`,
          `DPR: ${dpr.toFixed(1)}`,
          `Content: ${contentRect.w.toFixed(0)}×${contentRect.h.toFixed(0)}`,
          ...(config.debug.showPhase && phaseStats ? [
            `Phase: ${phaseStats.phase}`,
            `Frames: ${phaseStats.frameCount}`,
            `Ramp: ${phaseStats.rampFactor.toFixed(2)}`
          ] : []),
          ...(config.debug.showJitter && stillnessStats ? [
            `Still: ${stillnessStats.isStill ? 'YES' : 'NO'}`,
            `Vel: ${stillnessStats.currentVelocity.toFixed(1)}px/s`
          ] : [])
        ];
        
        debugInfo.forEach((text, i) => {
          const y = 25 + i * 18;
          ctx.strokeText(text, 10, y);
          ctx.fillText(text, 10, y);
        });
      }
      ctx.restore();
    }

    
    // Reduce console spam - only log every 60 frames
    if (frameCountRef.current % 60 === 0) {
      console.log(`[Overlay] ready { framesApplied: ${frameCountRef.current}, videoReady: true }`);
    }
  }, [getLatestFrame, getFrameAtTime, isLiveCapture, visible, opacity, handsOnly, showSkeleton, replayDirectMap]);

  // FIX: rVFC-only draw loop that respects video state
  const scheduleNext = useCallback(() => {
    const video = videoRef?.current;
    
    // Only schedule next frame if video is playing and overlay is visible
    if (video && visible && !video.paused && !video.ended) {
      // Use requestVideoFrameCallback for frame-accurate drawing
      video.requestVideoFrameCallback(() => {
        draw();
        scheduleNext(); // Schedule next frame
      });
    }
  }, [visible, draw]);

  const stopDrawing = useCallback(() => {
    // rVFC doesn't need explicit cancellation like rAF
    // The video element handles cancellation automatically
  }, []);

  // FIX: Define event handlers with stable references for proper cleanup
  const startDrawing = useCallback(() => {
    const video = videoRef?.current;
    if (video && visible && !video.paused && !video.ended) {
      scheduleNext();
    }
  }, [visible, scheduleNext]);

  // Reset smoothing when switching videos or modes
  useEffect(() => {
    resetSmoothingState();
    frameCountRef.current = 0;
    lastFrameTimeRef.current = performance.now();
    lastMediaTimeRef.current = 0;
    
    // Reset jitter reduction instances
    stillnessDetectorRef.current?.reset();
    phaseControllerRef.current?.reset();
    
    console.log('[OverlayCanvas] Reset all smoothing state for mode change');
  }, [isLiveCapture]);

  useEffect(() => {
    // FIX: Guard video ref access
    const video = videoRef?.current;
    
    // TRIPWIRE: Enhanced overlay initialization diagnostics with first paint
    const framesBuffered = getLatestFrame ? 1 : 0;
    
    if (!video || !visible) return;
    
    // Wait for video loadedmetadata before sizing canvas
    const resizeCanvas = () => {
      const canvas = canvasRef?.current;
      if (!canvas || !video) return;
      
      // Size canvas to rendered video dimensions
      const rect = video.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      canvas.style.position = 'absolute';
      canvas.style.top = '0';
      canvas.style.left = '0';
      canvas.style.pointerEvents = 'none';
      canvas.style.zIndex = '10';
      canvas.style.opacity = '1';
    };
    
    if (video.readyState >= 1) {
      resizeCanvas();
      if (replayDirectMap) draw();
    } else {
      const onMeta = () => {
        resizeCanvas();
        if (replayDirectMap) draw();
      };
      video.addEventListener('loadedmetadata', onMeta, { once: true });
    }

    // FIX: Listen to video events for rVFC control
    video.addEventListener('play', startDrawing);
    video.addEventListener('pause', stopDrawing);
    video.addEventListener('ended', stopDrawing);
    video.addEventListener('seeking', stopDrawing);
    video.addEventListener('seeked', startDrawing);
    // Replay overlay: redraw on seek/timeupdate while paused (rVFC only runs when playing)
    const onTimeUpdateReplay = () => {
      if (replayDirectMap && video.paused) draw();
    };
    if (replayDirectMap) {
      video.addEventListener('timeupdate', onTimeUpdateReplay);
      video.addEventListener('seeked', onTimeUpdateReplay);
    }

    // Start if already playing
    if (!video.paused && !video.ended) {
      startDrawing();
    }

    return () => {
      stopDrawing();
      if (video) {
        video.removeEventListener('play', startDrawing);
        video.removeEventListener('pause', stopDrawing);
        video.removeEventListener('ended', stopDrawing);
        video.removeEventListener('seeking', stopDrawing);
        video.removeEventListener('seeked', startDrawing);
        if (replayDirectMap) {
          video.removeEventListener('timeupdate', onTimeUpdateReplay);
          video.removeEventListener('seeked', onTimeUpdateReplay);
        }
      }
    };
  }, [visible, scheduleNext, stopDrawing, replayDirectMap, draw]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 2,
      }}
      aria-label={`Golf swing pose overlay - ${visible ? 'visible' : 'hidden'}, ${isLiveCapture ? 'live capture' : 'replay'} mode`}
      role="img"
      tabIndex={-1}
    />
  );
}